<template>
 <v-card class="ps-card" flat>
   <div class="ps-card__head">
     <v-icon small>mdi-image-multiple-outline</v-icon>
     Mockups
     <span class="ps-card__hint">Klick auf eine Karte kopiert die Mockup-Daten</span>
   </div>
   <v-card-text class="mockup-container">
     <StateDisplay :loading="loadingMockups"/>
     <v-row dense>
       <v-col v-for="mockup in mockups" :key="mockup.uuid" cols="6" sm="4" md="3">
         <v-card flat @click="() => copyMockupData(mockup.mockupData)" height="280px" class="ps-mockup-card">
           <span v-if="mockup.aspectRatio" class="ps-mockup-card__pill ps-mockup-card__pill--left">{{mockup.aspectRatio}}</span>
           <span class="ps-mockup-card__pill ps-mockup-card__pill--right">{{mockup.orientation}}</span>
           <span class="ps-mockup-card__name">
             {{mockup.name}}
             <v-btn
               icon x-small class="ps-mockup-card__delete"
               :loading="deletingId === mockup.uuid"
               @click.stop="requestDelete(mockup)"
             >
               <v-icon small color="#fff">mdi-delete-outline</v-icon>
             </v-btn>
           </span>
           <v-img height="280px" cover :src="mockup.thumbnail"/>
         </v-card>
       </v-col>
     </v-row>
   </v-card-text>

   <ConfirmDialog
     :show="!!mockupToDelete && !usageBlockMessage"
     :on-cancel="() => (mockupToDelete = null)"
     :on-accept="confirmDelete"
     icon="mdi-trash-can-outline"
     title="Mockup löschen"
     :message="`„${mockupToDelete && mockupToDelete.name}“ wird endgültig gelöscht. Es wird aktuell nirgends verwendet (Print-Varianten & Fallbacks geprüft). Fortfahren?`"
     accept-text="LÖSCHEN"
     accept-color="error"
   />
   <ConfirmDialog
     :show="!!usageBlockMessage"
     :on-cancel="clearUsageBlock"
     :on-accept="clearUsageBlock"
     icon="mdi-alert-circle-outline"
     title="Mockup wird verwendet"
     :message="usageBlockMessage"
     accept-text="OK"
     accept-color="primary"
   />
 </v-card>
</template>

<script>
import StateDisplay from "@/components/StateDisplay";
import ConfirmDialog from "@/components/ConfirmDialog";
import {mapActions, mapGetters} from "vuex";
import {copyToClipboard} from "@/utils";
import {doc, getDoc} from "firebase/firestore";
import {db} from "../../../../fb";

export default {
  name: "AllMockups",
  components: {StateDisplay, ConfirmDialog},
  data() {
    return {
      isLoading: false,
      mockupToDelete: null,
      usageBlockMessage: "",
      deletingId: null,
    }
  },
  methods: {
    ...mapActions(["loadMockups", "deleteMockup"]),
    copyMockupData(data) {
      copyToClipboard(data)
    },
    /** Vor dem Löschen prüfen, ob das Mockup irgendwo referenziert wird:
     *  Print-Varianten (mockupPortrait/-Landscape) und Fallbacks. */
    async requestDelete(mockup) {
      this.deletingId = mockup.uuid;
      try {
        const usages = [];

        const printVariantsSnap = await getDoc(doc(db, "settings", "printVariants"));
        const printVariants = printVariantsSnap.data() || {};
        for (const variant of Object.values(printVariants)) {
          if (variant.mockupPortrait === mockup.uuid) {
            usages.push(`Print-Variante „${variant.name}" (Portrait)`);
          }
          if (variant.mockupLandscape === mockup.uuid) {
            usages.push(`Print-Variante „${variant.name}" (Landscape)`);
          }
        }

        const mockupSettingsSnap = await getDoc(doc(db, "settings", "mockups"));
        const fallbacks = mockupSettingsSnap.data()?.fallbacks || {};
        for (const [orientation, id] of Object.entries(fallbacks)) {
          if (id === mockup.uuid) {
            usages.push(`Fallback (${orientation})`);
          }
        }

        if (usages.length) {
          this.usageBlockMessage =
            `„${mockup.name}" kann nicht gelöscht werden — es wird verwendet von: ` +
            usages.join(", ") + ". Erst dort ersetzen, dann löschen.";
        } else {
          this.mockupToDelete = mockup;
        }
      } finally {
        this.deletingId = null;
      }
    },
    async confirmDelete() {
      const mockup = this.mockupToDelete;
      this.mockupToDelete = null;
      this.deletingId = mockup.uuid;
      try {
        await this.deleteMockup(mockup.uuid);
      } finally {
        this.deletingId = null;
      }
    },
    clearUsageBlock() {
      this.usageBlockMessage = "";
    },
  },
  computed: {
    ...mapGetters(["loadingMockups" ,"mockups"])
  },
  mounted() {
    this.loadMockups()
  }
}
</script>

<style lang="scss" scoped>
.mockup-container {
  min-height: 600px;
}

.ps-mockup-card__name {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.ps-mockup-card__delete {
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
}
</style>
